import { apiConfig } from "@grindex-common/helpers/apiConfig";

import { isNotNullishOrEmpty } from "@grindex-common/helpers/typeHelpers";
import { formatQueryParams } from "@grindex-client/common/js/helpers/query-parser";
import type { FacetQuery, SearchQueryFacet } from "@grindex-client/components/Search/Query/facet-query";

type SearchQueryData = {
	[x: string]: unknown;
	currentpageid: string | undefined;
	categoryid: number | undefined;
	categories: null;
	facetids: string[];
	onlyfetchchildren: boolean;
	page: number;
	isbrandproductsearch: boolean;
	pagesize: number;
	order: string | null;
	utm_source: null;
	utm_medium: null;
	utm_campaign: null;
	utm_content: null;
	get: (member: any) => any[];
	set: (member: any, value: any) => void;
};

export default class SearchQueryBase implements FacetQuery {
	data: { totalItems: number; facetGroups: never[]; selectedFacets: string[]; get: (member: any) => any[] };
	queryData: SearchQueryData;
	constructor() {
		this.data = {
			totalItems: 0,
			facetGroups: [],
			selectedFacets: [],
			get: function (member) {
				return [member];
			},
		};

		this.queryData = {
			currentpageid: apiConfig.apiNode,
			categoryid: window.config.search?.categoryId,
			categories: null,
			facetids: [],
			onlyfetchchildren: window.config.search?.onlyFetchChildren ? true : false,
			page: 1,
			isbrandproductsearch: false,
			pagesize: 24,
			order: window.config.search?.order ? window.config.search?.order : null,
			utm_source: null,
			utm_medium: null,
			utm_campaign: null,
			utm_content: null,
			get: function (member) {
				return [member.toLowerCase()];
			},
			set: function (member, value) {
				this[member.toLowerCase()] = value;
			},
		};
	}

	clearFacets() {
		this.queryData.facetids = [];
		this.queryData["keyword"] = "";
		this.resetPage();
	}

	updateFacets(facet: SearchQueryFacet) {
		if (facet.selected) {
			if (isNaN(facet.id as unknown as number)) {
				this.queryData.facetids.push(facet.id);
			} else {
				this.queryData.facetids.push(facet.id.toString());
			}
		} else {
			this.queryData.facetids = this.queryData.facetids.filter((facetId) => {
				return facetId !== facet.id;
			});
		}

		this.data.selectedFacets = this.queryData.facetids;

		this.resetPage();
	}

	setCategories() {
		const categoriesInput = document.getElementById("categories") as HTMLInputElement | null;

		if (categoriesInput && categoriesInput.value) {
			const facetId = Number(categoriesInput.value);

			if (facetId) {
				this.queryData.facetids.push(facetId.toString());
			}
		}
	}

	hasFacetIds(): boolean {
		return isNotNullishOrEmpty(this.queryData.facetids);
	}

	getPagingData() {
		return {
			totalItems: this.data.totalItems,
			page: this.queryData.page,
			pagesize: this.queryData.pagesize,
		};
	}

	setValue(value: unknown, member: string) {
		if (value) {
			this.queryData[member] = value;
		}
	}

	setQueryFromUrl() {
		const queryParams = new URLSearchParams(window.location.search);

		for (const [rawName, value] of queryParams.entries()) {
			if (!rawName || !value) {
				continue;
			}

			const mappedName = rawName === "query" ? "keyword" : rawName;
			const lowerCaseName = mappedName.toLowerCase();
			const currentValue = this.queryData[lowerCaseName];
			const isArrayParameter = Array.isArray(currentValue);

			if (!currentValue) {
				this.queryData[lowerCaseName] = isArrayParameter ? [value] : value;
				continue;
			}

			if (isArrayParameter) {
				(currentValue as unknown[]).push(value);
			}
		}
	}

	resetPage() {
		this.setPage(1);
	}

	getQuery() {
		this.queryData.page = this.queryData.page || 1;
		this.queryData.pagesize = this.queryData.pagesize || 24;

		return this.queryData;
	}

	setPage(page: number) {
		this.queryData.page = page;
	}

	setPageSize(pageSize: number) {
		this.queryData.pagesize = pageSize;
		this.resetPage();
	}

	getQueryString() {
		return formatQueryParams(this.queryData, true, true);
	}

	propertyHasValue(value: unknown) {
		if (typeof value === "string") {
			return value.length > 0;
		}

		if (typeof value === "object") {
			const valueIsNotNull = value !== null;

			if (valueIsNotNull && Array.isArray(value)) {
				return value.length > 0;
			}

			return value !== null;
		}

		return typeof value !== "undefined" && typeof value !== "function";
	}

	getParsedQueryObject() {
		const parsedQuery: Record<string, unknown> = {};

		Object.keys(this.queryData)
			.filter((key) => {
				return this.propertyHasValue(this.queryData[key]);
			})
			.map((key) => {
				parsedQuery[key] = this.queryData[key];
			});

		return parsedQuery;
	}
}
